/**
 * Device-side store.
 * - In cloud mode it's a CACHE + an outbox of changes waiting to sync.
 *   The server is the source of truth; this exists so the app opens instantly
 *   and keeps working with no signal (hospital basements, lifts...).
 * - In demo mode it's the only storage.
 */
import type { CountSession, Kick, Locale, Profile } from '../domain/types';
import type { JournalEntry } from '../domain/journal';
import type { Moment, MomentKind } from '../domain/moments';

export type PendingOp =
  | { op: 'profile.save'; profile: Profile }
  | { op: 'consent.add'; kinds: string[]; version: string }
  | { op: 'kick.add'; kick: Kick }
  | { op: 'kick.remove'; clientId: string }
  | { op: 'session.upsert'; session: CountSession }
  | { op: 'journal.add'; entry: JournalEntry }
  | { op: 'journal.remove'; clientId: string }
  | { op: 'moment.save'; moment: Moment }
  | { op: 'moment.remove'; kind: MomentKind };

interface Snapshot {
  version: 1;
  ownerId: string | null;       // which signed-in user this cache belongs to
  locale: Locale | null;        // chosen before sign-in
  profile: Profile | null;
  kicks: Kick[];
  sessions: CountSession[];
  journal: JournalEntry[];
  moments: Moment[];
  outbox: PendingOp[];
  ui: { coachHome?: boolean; coachTap?: boolean; trialPaywallSeen?: boolean; demoTrialStartedAt?: number; jotNudges?: number; jotLearned?: boolean; startTourDone?: boolean };
}

const KEY = 'bumpkick.store.v1';
const empty = (): Snapshot => ({ version: 1, ownerId: null, locale: null, profile: null, kicks: [], sessions: [], journal: [], moments: [], outbox: [], ui: {} });

function read(): Snapshot {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw) as Snapshot;
      if (s.version === 1) return { ...empty(), ...s };
    }
  } catch { /* blocked storage (private mode) → memory only */ }
  return empty();
}

let snap = read();

function write(): void {
  try { localStorage.setItem(KEY, JSON.stringify(snap)); } catch { /* quota/blocked: keep in memory */ }
}

export const localStore = {
  get: (): Readonly<Snapshot> => snap,

  setLocale(l: Locale) { snap.locale = l; write(); },
  setProfile(p: Profile | null) { snap.profile = p; write(); },
  setUi(patch: Partial<Snapshot['ui']>) { snap.ui = { ...snap.ui, ...patch }; write(); },

  /** Bind cache to a user. A different user signing in on the same phone must never see the previous user's data. */
  /** Remove queued operations of one kind (e.g. a profile typed while trying the app, when the account already has one). */
  dropOps(kind: PendingOp['op']) { snap.outbox = snap.outbox.filter(o => o.op !== kind); write(); },

  bindOwner(userId: string | null) {
    if (snap.ownerId && userId && snap.ownerId !== userId) {
      const { locale, ui } = snap;
      snap = { ...empty(), locale, ui: { coachHome: ui.coachHome, coachTap: ui.coachTap } };
    }
    snap.ownerId = userId;
    write();
  },

  addKick(k: Kick) { snap.kicks.push(k); write(); },
  removeKick(clientId: string) { snap.kicks = snap.kicks.filter(k => k.clientId !== clientId); write(); },
  upsertSession(s: CountSession) {
    const i = snap.sessions.findIndex(x => x.clientId === s.clientId);
    if (i >= 0) snap.sessions[i] = s; else snap.sessions.push(s);
    write();
  },
  addJournal(e: JournalEntry) { snap.journal.push(e); write(); },
  removeJournal(clientId: string) { snap.journal = snap.journal.filter(e => e.clientId !== clientId); write(); },
  saveMoment(m: Moment) { snap.moments = [...snap.moments.filter(x => x.kind !== m.kind), m]; write(); },
  removeMoment(kind: MomentKind) { snap.moments = snap.moments.filter(x => x.kind !== kind); write(); },
  replaceMoments(list: Moment[]) {
    const pending = new Map(snap.outbox.flatMap(o => (o.op === 'moment.save' ? [[o.moment.kind, o.moment] as const] : [])));
    const removed = new Set(snap.outbox.flatMap(o => (o.op === 'moment.remove' ? [o.kind] : [])));
    const m = new Map(list.filter(x => !removed.has(x.kind)).map(x => [x.kind, x]));
    pending.forEach((v, k) => m.set(k, v));
    snap.moments = [...m.values()];
    write();
  },
  replaceJournal(entries: JournalEntry[]) {
    const pending = new Map(snap.outbox.flatMap(o => (o.op === 'journal.add' ? [[o.entry.clientId, o.entry] as const] : [])));
    const removed = new Set(snap.outbox.flatMap(o => (o.op === 'journal.remove' ? [o.clientId] : [])));
    const m = new Map(entries.filter(e => !removed.has(e.clientId)).map(e => [e.clientId, e]));
    pending.forEach((v, id) => m.set(id, v));
    snap.journal = [...m.values()].sort((a, b) => a.occurredAt - b.occurredAt);
    write();
  },
  replaceData(kicks: Kick[], sessions: CountSession[]) {
    // keep anything still waiting to upload so it isn't lost
    const pendingKicks = new Map(snap.outbox.flatMap(o => (o.op === 'kick.add' ? [[o.kick.clientId, o.kick] as const] : [])));
    const pendingSessions = new Map(snap.outbox.flatMap(o => (o.op === 'session.upsert' ? [[o.session.clientId, o.session] as const] : [])));
    const removed = new Set(snap.outbox.flatMap(o => (o.op === 'kick.remove' ? [o.clientId] : [])));
    const k = new Map(kicks.filter(x => !removed.has(x.clientId)).map(x => [x.clientId, x]));
    pendingKicks.forEach((v, id) => k.set(id, v));
    const s = new Map(sessions.map(x => [x.clientId, x]));
    pendingSessions.forEach((v, id) => s.set(id, v));
    snap.kicks = [...k.values()];
    snap.sessions = [...s.values()];
    write();
  },

  enqueue(op: PendingOp) { snap.outbox.push(op); write(); },
  peek: (): PendingOp | undefined => snap.outbox[0],
  dequeue() { snap.outbox.shift(); write(); },

  wipe() {
    snap = empty();
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  }
};

export const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
